import * as Sentry from "@sentry/react";
import { format } from "date-fns";
import { useAtom } from "jotai";
import {
  Bug,
  Calendar,
  ListChecks,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Pin,
  PinOff,
  Share,
  SquarePen,
  Trash2,
  Zap,
} from "lucide-react";
import { useGateValue } from "@statsig/react-bindings";
import { useTaskGates } from "@/components/userTasks/useTaskGates.ts";

import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";

import { messageListScrollPositionAtom } from "@/atoms/messagesAtoms.ts";
import { buildCalendarUrl } from "@/components/calendars/calendar-url.ts";
import { ErrorMessage } from "@/components/common/Error.tsx";
import { Archive, Unarchive } from "@/components/icons/customIcons.tsx";
import {
  clearCanvasUpdateUnseen,
  useCanvasUpdateUnseen,
} from "@/components/canvas/canvasUpdateBadge.ts";
import { useCanvasActionContext } from "@/components/canvas/canvasContext.tsx";
import { toUrnString } from "@/types/urn.ts";
import { useThreadCanvas } from "@/components/threads/useThreadCanvas.tsx";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { isFloatingLayerOpen } from "@/components/keyboard/floatingLayers.ts";
import { useEscapeToBack } from "@/components/keyboard/useEscapeToBack.ts";
import { useShortcut } from "@/components/keyboard/useShortcut.ts";
import { SplitPanelFrame } from "@/components/layout/SplitPanel/SplitPanelFrame.tsx";
import {
  PanelBreadcrumb,
  panelHeaderControlClass,
  panelHeaderCrumbTitleClass,
  panelTrailingIconClass,
  SplitPanelBackButton,
  SplitPanelHeader,
} from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { pageColumnClasses } from "@/components/layout/contentLayout.tsx";
import { HeaderUtilitySlot } from "@/components/layout/HeaderUtilitySlot.tsx";
import { SiteHeaderTitle } from "@/components/layout/ViewWrapper.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import {
  DebugLinkMenu,
  useEventView,
  useEventViewHotkeys,
} from "@/components/messages/Omnibox/DebugLinkButton.tsx";
import { MessageList } from "@/components/messages/MessageList.tsx";
import { ThreadTasksDialog } from "@/components/tasks/ThreadTasksDialog.tsx";
import { CardFacepile } from "@/components/threads/CardFacepile.tsx";
import { BAR_TIER_CLASS, isInBar } from "@/components/layout/SplitPanel/headerActionTiers.ts";
import {
  type PanelHeaderAction,
  PanelHeaderActionButton,
  PanelHeaderMenu,
  PanelHeaderMenuItem,
  splitPanelHeaderActions,
  usePanelHeaderWidth,
} from "@/components/layout/SplitPanel/panelHeaderActions.tsx";
import { RoutineTriggeredBadge, ThreadStatusBadge } from "@/components/threads/shared.tsx";
import { ThreadContactsMenuItem, useThreadContacts } from "@/components/threads/ThreadContacts.tsx";
import { ThreadDeleteDialog } from "@/components/threads/ThreadDeleteDialog.tsx";
import { useShareThread, useTogglePin } from "@/components/threads/ThreadItemActions.tsx";
import { getThreadName, getThreadStatus } from "@/components/threads/util.ts";
import { Button } from "@/components/ui/button.tsx";
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu.tsx";
import { Input } from "@/components/ui/input.tsx";
import { parseUnitsExhaustedError } from "@/components/billing/UnitsExhaustedDialog.tsx";
import { archiveToast, errorToast } from "@/components/ui/toast.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { cn } from "@/lib/utils.ts";
import { routineRouteId } from "@/shared/routines.ts";
import { ROUTINES_BASE_PATH } from "@/views/Routines.tsx";
import { useBackNavigation } from "@/hooks/useBackNavigation.ts";
import { useTrackThreadDetailView } from "@/components/threads/hooks/useActiveThreadTracking.ts";
import { inlineRenameFieldClass, useInlineRename } from "@/components/threads/useInlineRename.ts";
import { useOffersDelete } from "@/shared/useOffersDelete.ts";
import { createNewTopicThread, useArchiveThread, useUnarchiveThread } from "@/hooks/useThreads.tsx";
import type { GetThreadResp } from "@/types/thread.ts";
import { isThreadSupersededByGroup } from "@/types/threadConfig.ts";

/**
 * Read order for the overflow menu, which is not the bar's order. The bar is a
 * left-to-right strip where position is about reach; the menu is a list, and a
 * thread's actions should come in the same sequence wherever they are met — so
 * this mirrors the list row's menu, with "New thread" ahead of them as the one
 * action here that is not about *this* thread.
 *
 * `archive` is here for the thread whose bar slot goes to Delete — its way back
 * drops into this menu and needs a place in the sequence. Every other thread
 * keeps it in the bar, where this order never applies.
 *
 * Anything unnamed keeps its array order after these — the extras (Tasks, Edit
 * routine, Calendar, the sidebar toggle) have no counterpart in the row's menu
 * to line up with. `delete` is absent because it is not sorted at all; it is
 * pinned last, behind a separator, at the render site.
 */
const MENU_ORDER = ["new-thread", "pin", "archive", "rename", "share"];
const menuRank = (key: string) => {
  const i = MENU_ORDER.indexOf(key);
  return i === -1 ? MENU_ORDER.length : i;
};

const HeaderActions = ({
  thread,
  onBack,
  onThreadGone,
  showCanvas,
  onRename,
}: {
  thread: GetThreadResp;
  onBack: () => boolean;
  /**
   * Leaves a thread that no longer exists — the one exit there is no coming
   * back through, so the host drops its address rather than keeping it behind
   * the destination. Returns what `onBack` does: whether leaving is still the
   * caller's to do.
   */
  onThreadGone: () => boolean;
  /** Whether the canvas is available (gate-on desktop) — when true the header
   *  shows the show/hide-calendar toggle. */
  showCanvas: boolean;
  /** Puts the header's title into its edit state; owned by the panel, which
   *  renders both the title and this menu. */
  onRename: () => void;
}) => {
  // The header toggle flips visibility through the thread's canvas handle.
  const { isCanvasOpen, canvasActions } = useThreadCanvas(thread.id, {
    enabled: showCanvas,
  });
  // The surrounding surface's canvas — the column beside this conversation when
  // the shell hosts it. Distinct from `canvasActions` above, which is this
  // thread's own handle and drives the panel's visibility toggle; header actions
  // that *present* something go to whichever canvas is actually on screen, and
  // it is `undefined` where there is none (mobile).
  const canvas = useCanvasActionContext();
  const toggleCanvas = () => {
    if (isCanvasOpen) {
      canvasActions.close();
    } else {
      // Opening the panel clears the unseen badge — the interaction outcome lives
      // in the handler; the effect below only covers an update that lands while
      // the panel is already open.
      canvasActions.open();
      clearCanvasUpdateUnseen(thread.id);
    }
  };

  // Badge the canvas toggle when the server pushed a new canvas target the user
  // hasn't looked at yet (see `thread_canvas:update`). Opening the canvas clears
  // it — we draw attention, never auto-open.
  const canvasUpdateUnseen = useCanvasUpdateUnseen(thread.id);
  useEffect(() => {
    if (isCanvasOpen && canvasUpdateUnseen) clearCanvasUpdateUnseen(thread.id);
  }, [isCanvasOpen, canvasUpdateUnseen, thread.id]);
  const canvasHasUpdate = canvasUpdateUnseen && !isCanvasOpen;

  // Anchors the header measurement. The trigger is the one element here
  // guaranteed to be inside the header whatever the width happens to be showing.
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  // Delete is in the bar or in the menu depending on the thread, and the
  // confirmation has to hand focus back to whichever one opened it.
  const deleteTriggerRef = useRef<HTMLButtonElement>(null);
  const headerWidth = usePanelHeaderWidth(menuTriggerRef);
  // Which modality last drove the overflow menu, read on close to decide
  // whether focus should return to the trigger. Tracked on both the trigger and
  // the content so a menu opened by mouse but dismissed with Escape still counts
  // as keyboard — the user's hands moved, and focus should follow them.
  const lastInputWasPointer = useRef(false);
  const notePointerUse = () => {
    lastInputWasPointer.current = true;
  };
  const noteKeyboardUse = () => {
    lastInputWasPointer.current = false;
  };
  // Rename cannot run from `onClick` like the rest of these actions: its result
  // is a focused text field, and Radix tears its focus scope down after the
  // handler — the blur that deals out landed on the title input the instant it
  // mounted, leaving the field open but focus on `<body>`, so the first
  // keystroke went nowhere and Escape reached the panel's "back to list". So the
  // selection is recorded here and acted on once the menu has actually closed.
  const renameSelectedRef = useRef(false);
  // `DebugLinkMenu` gates itself, but the menu has to know whether that row will
  // materialize before deciding it has anything worth opening for.
  const showDebugOptions = useGateValue("debug_link_menu");
  // Either task feature has a thread task view; `tasks-v2` reads it from the list.
  const { any: tasksEnabled } = useTaskGates();

  const archiveMutation = useArchiveThread({ threadId: thread.id });
  const unarchiveMutation = useUnarchiveThread({ threadId: thread.id });
  const { share: handleShare, isPending: sharePending } = useShareThread({ thread });
  const navigateBack = useBackNavigation();
  const [, setLocation] = useLocation();

  // Lives outside the dropdown below: Radix unmounts the menu's content when an
  // item is selected, which would take a dialog rendered inside it down in the
  // same frame. The menu item only flips this.
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [tasksOpen, setTasksOpen] = useState(false);
  const { isPinned, togglePin, isPending: pinPending } = useTogglePin({ thread });

  // Canonical focus link for today. This entry only shows where there's no canvas
  // to move, so it falls through the focus boundary to the `/calendar` page.
  const handleOpenCalendar = () => {
    setLocation(buildCalendarUrl({ date: format(new Date(), "yyyy-MM-dd") }));
  };

  // Routine-based threads carry the bound routine in `routineEntId`: a user
  // routine's ent id, or the `system:<slug>` sentinel for system
  // routines. The edit screen routes on the bare id/slug (matching the
  // routines list), so strip the sentinel prefix before navigating.
  const editRouteId = thread.routineEntId ? routineRouteId(thread.routineEntId) : undefined;

  /**
   * Open the routine this thread runs on: drilled onto the canvas beside the
   * conversation, falling back to the routines screen where there is no canvas
   * (mobile, gate-off).
   *
   * The canvas card is the same {@link RoutineDetail} that screen renders, so
   * nothing is given up by staying — and staying is the point. Editing the
   * routine is a thing you do *about* this thread, and sending the user to
   * another screen for it meant leaving the conversation that prompted it and
   * needing a `?from=` to find the way back.
   *
   * Addressed from `routineEntId` rather than the stripped `editRouteId`,
   * because the URN keeps the `system:` sentinel that the route drops: a canvas
   * routine is `urn:bb:routine:system:<slug>` or `urn:bb:routine:<uuid>`, and a
   * bare slug parses as neither.
   */
  const handleEditRoutine = () => {
    if (!editRouteId || !thread.routineEntId) return;
    const urn = toUrnString({ type: "routine", id: thread.routineEntId });
    // `duplicate` counts as opened — the routine's card is already on top. Every
    // other decline leaves it unshown, so fall through to the screen that can
    // show it rather than swallowing the click.
    const presented = canvas?.push(urn);
    if (presented === "pushed" || presented === "duplicate") return;
    // Pass the thread as the return target so the editor's back button comes
    // back here instead of the routines list.
    //
    // The settings copy of the editor by name, which is where this already
    // ended up — `/routines/:id` used to redirect here and now belongs to the
    // canvas. It has to be this one: the return link is chrome the editor draws
    // only when it owns the screen, and the canvas supplies its own header,
    // whose way back is the routines list rather than this conversation.
    const from = encodeURIComponent(`/threads/${thread.id}`);
    setLocation(`${ROUTINES_BASE_PATH}/${editRouteId}?from=${from}`);
  };

  const handleArchive = async () => {
    // make sure this completes before we navigate back, otherwise there's a race condition
    // where the threadlist refreshes and gets stale data
    if (thread.status !== "completed") {
      try {
        await archiveMutation.mutateAsync();
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: "ThreadDetailPanel", operation: "archiveThread" },
          extra: { threadId: thread.id },
        });
        errorToast("Failed to archive thread");
        // Leave the user on the thread rather than navigating away from a failure.
        return;
      }
      archiveToast();
    }

    const goBack = onBack();
    if (goBack) {
      navigateBack();
    }
  };
  // Stays on the thread the user just reopened — the opposite of archive, which
  // earns its navigate-back by removing the thread from the feed.
  const handleUnarchive = async () => {
    try {
      await unarchiveMutation.mutateAsync(thread.id);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: "ThreadDetailPanel", operation: "unarchiveThread" },
        extra: { threadId: thread.id },
      });
      errorToast("Failed to unarchive thread");
    }
  };

  // Same shape as archive's: a host that owns the column has already put it
  // back, and popping history on top of that would undo the host's own
  // navigation — which landed the user right back on the thread they had just
  // deleted, now a 403 the panel rendered as a raw error (BIG-7845).
  const handleDeleted = useCallback(() => {
    if (onThreadGone()) navigateBack();
  }, [onThreadGone, navigateBack]);

  // `modifierOnly` on the full-screen thread page: ⌘D archives deliberately, but
  // the bare `e` (a triage shortcut for the list/panel) shouldn't silently
  // archive the thread you're reading just because focus left the composer.
  useShortcut(
    "threads.archive",
    () => {
      void handleArchive();
    },
    { deps: [handleArchive], modifierOnly: true },
  );

  const handleNewThread = async () => {
    try {
      const thread = await createNewTopicThread({
        title: "",
        artifactIds: [],
      });
      if (!thread.id) {
        throw new Error("Failed to create new thread - no thread id");
      }
      window.history.pushState(null, "", `/threads/${thread.id}`);
    } catch (error) {
      console.error("Failed to create new thread", error);
      // The upgrade dialog already explains a refusal.
      if (!parseUnitsExhaustedError(error)) errorToast("Failed to create new thread");
    }
  };

  const threadName = getThreadName(thread);
  const isArchived = thread.status === "completed";
  const offersDelete = useOffersDelete(thread);

  // ── Header actions (declarative) ────────────────────────────────────
  // One list feeds both the bar and the overflow menu, so the two cannot
  // disagree about what an action is called, does, or reports.
  //
  // Array order is the bar's left-to-right order and the menu's top-to-bottom
  // order at the same time.
  //
  // On the canvas toggle's glyph: it names the action on the LEFT column
  // ("expand the conversation"), where the previous PanelRight* pair named it on
  // the right one ("close the canvas"). Same physical motion, opposite
  // grammatical subject — which is why Open/Close invert alongside the swap.
  // Read the chevron, not the suffix: PanelLeftOpen points right (the column
  // grows), PanelLeftClose points left (it retreats). Revert both halves
  // together if this ever flips back.
  const allActions: PanelHeaderAction[] = [
    {
      key: "new-thread",
      icon: SquarePen,
      label: "New thread",
      onClick: handleNewThread,
      statsigName: "thread-detail-new-thread",
      tier: "2xl",
    },
    {
      key: "share",
      icon: Share,
      label: "Share thread",
      onClick: handleShare,
      disabled: sharePending,
      statsigName: "thread-detail-share",
      tier: "3xl",
    },
    {
      // Stays in the header once archived, rather than dropping out of it. The
      // Routines and All filters both list `completed` (= archived) threads, so
      // an archived thread stays one click away in the feed — and gating this on
      // `visible` took it out of the overflow menu too, leaving that thread with
      // no archive control anywhere in its own view (BIG-6876).
      //
      // What it does give up, for the threads `useOffersDelete` answers for, is
      // the bar slot — to Delete, as their rows in the list do. Clearing the
      // thread out of the way is what this control is for, and there is nowhere
      // left to file one that is already filed. `never` rather than
      // `visible: false` is that same BIG-6876 rule: the way back has to stay
      // somewhere, and here that is the menu.
      key: "archive",
      icon: isArchived ? Unarchive : Archive,
      label: isArchived ? "Unarchive thread" : "Archive thread",
      onClick: isArchived ? handleUnarchive : handleArchive,
      disabled: archiveMutation.isPending || unarchiveMutation.isPending,
      statsigName: isArchived ? "thread-detail-unarchive" : "thread-detail-archive",
      tier: offersDelete ? "never" : "always",
    },
    {
      // Declared beside Archive because that is the slot it takes over: array
      // order is the bar's left-to-right order, so the trash arrives exactly
      // where the archive control had been. Menu order is not at
      // stake — this is pinned last there, behind a separator, at the render
      // site, because it is the one action a thread offers that cannot be
      // undone and it should not sit under the user's thumb.
      //
      // Red only in the bar. In the menu it is one row among others, set apart
      // by that place behind the separator instead.
      key: "delete",
      icon: Trash2,
      label: "Delete thread",
      onClick: () => setConfirmDeleteOpen(true),
      // Named apart when promoted, as the row and card surfaces name theirs:
      // one event for both placements cannot answer whether the bar slot is the
      // thing being used.
      statsigName: offersDelete ? "thread-detail-primary-delete" : "thread-detail-delete",
      iconClassName: offersDelete ? "text-destructive" : undefined,
      tier: offersDelete ? "always" : "never",
    },
    {
      key: "edit-routine",
      icon: Zap,
      label: "Edit routine",
      onClick: handleEditRoutine,
      visible: !!editRouteId,
      statsigName: "thread-detail-edit-routine",
      tier: "never",
    },
    {
      // Fallback calendar access when the split canvas is unavailable (mobile).
      // Mutually exclusive with `canvas-toggle` via the inverse `!showCanvas` /
      // `showCanvas` gates.
      key: "calendar",
      icon: Calendar,
      label: "Calendar",
      onClick: handleOpenCalendar,
      visible: !showCanvas,
      statsigName: "thread-detail-calendar",
      tier: "never",
    },
    {
      key: "canvas-toggle",
      icon: isCanvasOpen ? PanelLeftOpen : PanelLeftClose,
      label: isCanvasOpen ? "Expand sidebar" : "Minimize sidebar",
      onClick: toggleCanvas,
      visible: showCanvas,
      statsigName: "split-pane-toggle",
      badge: canvasHasUpdate,
      // Holds its place beside Archive at every width the toggle exists at.
      // Widening the column is the answer to a cramped one, so the control that
      // does it is the last thing that should cost a click when the column is
      // narrow. Mobile drops it through `visible` instead — there is no second
      // pane there to expand.
      tier: "always",
    },
    {
      // Menu-only, and beside Pin: both change what the thread *is* rather than
      // what is being done with it. The header title also takes a direct click
      // to edit, but that affordance is invisible until hovered — this is where
      // a user who has not discovered it will look.
      //
      // Menu-only is load-bearing, not just a placement: this `onClick` records
      // the choice and the rename is started by the menu's `onCloseAutoFocus`
      // (see `renameSelectedRef`). A bar rendering of this action would call
      // `onClick` with no menu to close behind it, and the button would do
      // nothing. Keep `tier: "never"`.
      key: "rename",
      icon: Pencil,
      label: "Rename thread",
      onClick: () => {
        renameSelectedRef.current = true;
      },
      statsigName: "thread-detail-rename",
      tier: "never",
    },
    {
      // Menu-only at every width (BIG-7196): pinning is a deliberate, occasional
      // act, and the bar is for the controls a thread is actually worked with.
      key: "pin",
      icon: isPinned ? PinOff : Pin,
      label: isPinned ? "Unpin thread" : "Pin thread",
      onClick: togglePin,
      disabled: pinPending,
      statsigName: isPinned ? "thread-detail-unpin" : "thread-detail-pin",
      tier: "never",
    },
    {
      key: "tasks",
      icon: ListChecks,
      label: "Tasks",
      // Blue, as on the composer's tasks toggle and the threads panel's
      // account-wide one — the app's mark for an internal-only affordance.
      iconClassName: "text-blue-600",
      onClick: () => setTasksOpen(true),
      visible: tasksEnabled,
      statsigName: "thread-detail-tasks",
      tier: "never",
    },
  ];

  // Contacts has no entry in the action list — its bar form is the facepile, at
  // the `xl` tier — so it follows the same rule by hand.
  const showContactsRow =
    !isInBar("xl", headerWidth) &&
    ((thread.card?.participants?.length ?? 0) > 0 ||
      (thread.card?.additionalParticipantCount ?? 0) > 0);

  // Contacts and the debug submenu are counted but not listed: they render into
  // the menu with no bar form, so they can never be promoted out of it. Contacts
  // in particular can never be the sole item — New thread and Share are
  // unconditional and outrank it, so any width narrow enough to drop the
  // facepile has already dropped both of them in.
  const { barActions, menuActions, soleAction, showMenu, menuItemCount } = splitPanelHeaderActions({
    actions: allActions,
    headerWidth,
    extraMenuItems: (showContactsRow ? 1 : 0) + (showDebugOptions ? 1 : 0),
  });

  // Delete leaves the list entirely — it is rendered last, behind a separator,
  // the same way the row's menu sets it apart. Sorting alone would not do it:
  // an unnamed action added later would sort after it.
  const orderedMenuActions = menuActions
    .filter((a) => a.key !== "delete")
    .sort((a, b) => menuRank(a.key) - menuRank(b.key));
  const deleteAction = menuActions.find((a) => a.key === "delete");

  // The debug submenu gets the same promotion a sole action gets, for the same
  // reason: a menu holding one thing costs a click and saves nothing.
  const soleDebug = menuItemCount === 1 && showDebugOptions;

  return (
    <>
      {barActions.map((a) => (
        <PanelHeaderActionButton
          key={a.key}
          action={a}
          ref={a.key === "delete" ? deleteTriggerRef : undefined}
        />
      ))}
      {/* Promoted out of a menu that would have held it alone, so it does not
          hide: at this width the menu is its only alternative, and there
          isn't one. */}
      {soleAction && <PanelHeaderActionButton action={soleAction} hideBelowTier={false} />}
      {soleDebug && (
        <DebugLinkMenu threadId={thread.id}>
          <Button
            variant="ghost"
            size="icon"
            className={panelHeaderControlClass}
            aria-label="Debug options"
            data-statsig-name="thread-detail-debug"
          >
            <Bug className="size-5 text-blue-600" />
          </Button>
        </DebugLinkMenu>
      )}
      {/* One menu for everyone, so the header the team sees is the header the
          people using the product see. */}
      <PanelHeaderMenu
        title={threadName}
        label="Open thread actions menu"
        show={showMenu}
        triggerRef={menuTriggerRef}
        statsigName="thread-detail-menu"
        onPointerDown={notePointerUse}
        onKeyDown={noteKeyboardUse}
        // Radix hands focus back to the trigger on close, and Chrome carries the
        // focused menu item's `:focus-visible` state over with it — so a menu
        // opened and dismissed with the mouse left a ring sitting on the button,
        // which `:focus-visible` styling alone does not prevent.
        //
        // Suppressed for pointer interactions only. Keeping the restore for
        // keyboard is not about the ring: this content is portaled to the
        // document root, so cancelling the restore unmounts it with nowhere for
        // focus to land, and Escape drops the user at the top of the document
        // instead of back on the control they opened.
        onCloseAutoFocus={(e) => {
          if (lastInputWasPointer.current || renameSelectedRef.current) e.preventDefault();
          if (renameSelectedRef.current) {
            renameSelectedRef.current = false;
            onRename();
          }
        }}
      >
        {showContactsRow && (
          <ThreadContactsMenuItem thread={thread} statsigName="thread-detail-contacts" />
        )}
        {orderedMenuActions.map((a) => (
          <PanelHeaderMenuItem key={a.key} action={a} />
        ))}
        <DebugLinkMenu threadId={thread.id}>
          <DropdownMenuItem>
            <Bug className="size-4 text-blue-600" />
            Debug options
          </DropdownMenuItem>
        </DebugLinkMenu>
        {/* Last, and behind a separator, exactly as the list row's menu sets it
              apart: it is the one action here that cannot be undone. */}
        {deleteAction && (
          <>
            <DropdownMenuSeparator />
            <PanelHeaderMenuItem action={deleteAction} />
          </>
        )}
      </PanelHeaderMenu>
      <ThreadDeleteDialog
        thread={thread}
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        onDeleted={handleDeleted}
        restoreFocusRef={offersDelete ? deleteTriggerRef : menuTriggerRef}
        statsigName={
          offersDelete ? "thread-detail-primary-delete-confirm" : "thread-detail-delete-confirm"
        }
      />
      <ThreadTasksDialog
        threadId={thread.id}
        open={tasksOpen}
        onOpenChange={setTasksOpen}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          menuTriggerRef.current?.focus();
        }}
      />
    </>
  );
};

/** Facepile rendered in the thread header, positioned directly to the left of
 *  `HeaderActions`. Links to the thread's participant list. */
const ThreadHeaderFacepile = ({
  thread,
  className,
}: {
  thread: GetThreadResp;
  className?: string;
}) => {
  const { participants, additional, contactsHref } = useThreadContacts(thread);
  // No empty guard: `CardFacepile` already renders nothing when there is nobody.
  return (
    <CardFacepile
      participants={participants}
      additional={additional}
      className={className}
      href={contactsHref}
    />
  );
};

const ThreadTitle = ({
  thread,
  isRenaming,
  onRenameStart,
  onRenameDone,
}: {
  thread: GetThreadResp;
  isRenaming: boolean;
  onRenameStart: () => void;
  onRenameDone: () => void;
}) => {
  // We force the thread name to one line and truncate, so we use a popover to show the full thread name on tap.
  // TODO: tooltips don't work on mobile out of the box - https://linear.app/big-basin-labs/issue/BIG-649/long-press-for-tooltips */}

  const threadName = getThreadName(thread);
  // Editing is held by the panel rather than here: the overflow menu can start
  // a rename too, and it is not inside this component.
  const { inputProps } = useInlineRename({
    threadId: thread.id,
    title: thread.title,
    isEditing: isRenaming,
    onDone: onRenameDone,
  });

  if (isRenaming) {
    // `field-sizing: content` (CSS) tells the browser to size the input to
    // its content natively, no JS measurement needed. Chrome 123+ / Safari
    // 17.4+ / Firefox 123+.
    return (
      <Input
        {...inputProps}
        className={cn(
          inlineRenameFieldClass,
          // Named rather than inherited here, because the resting title names
          // its own size too — `SiteHeaderTitle` carries `text-base`, so the
          // header's own inherited size is not what this replaces.
          //
          // The `md:` half is load-bearing: `Input`'s base carries `md:text-sm`,
          // and tailwind-merge keys on the modifier, so an unmodified
          // `text-base` cannot displace it.
          "w-auto max-w-full field-sizing-content font-medium text-base md:text-base leading-5",
        )}
      />
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <SiteHeaderTitle
          onClick={onRenameStart}
          // A thread is always something you opened from a list, so it wears the
          // drilled-in title rather than the display face a top-level page gets
          // — the shared class, not a copy of its values.
          //
          // pr-0 overrides the SiteHeaderTitle base `pr-4` so the gap to the
          // status badge is controlled by the parent flex's `gap-2` only,
          // matching the edit-mode input which has no right-padding.
          className={cn(
            panelHeaderCrumbTitleClass,
            "cursor-text rounded-sm pr-0 transition-colors hover:bg-muted/50",
          )}
        >
          {threadName}
        </SiteHeaderTitle>
      </TooltipTrigger>
      <TooltipContent className="text-sm px-2.5 py-1 rounded-sm w-fit max-w-[90vw]" side="bottom">
        Click to edit · {threadName}
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * The thread's status and routine marks, in one of exactly two forms: the full
 * pill where the words fit, and a bare glyph everywhere else. Never absent — an
 * alert the user has to open a menu to discover is not doing its job.
 *
 * Rendered twice by the header, once per placement, because the two sit under
 * different parents and CSS cannot move an element between parents. Exactly one
 * is ever displayed; the other is `display: none`, so assistive tech sees a
 * single indicator and no `aria-hidden` bookkeeping is needed.
 */
const ThreadIndicators = ({ thread }: { thread: GetThreadResp }) => {
  const showStatus =
    thread.status !== "draft" && thread.status !== "completed" && !!getThreadStatus(thread);
  const hasRoutineRun = !!thread.labels?.includes("routineRun");

  // Single config array — rendered twice (full + compact) to avoid duplicate push logic
  const badges = [
    showStatus && { Component: ThreadStatusBadge, key: "status" },
    hasRoutineRun && { Component: RoutineTriggeredBadge, key: "routine-triggered" },
  ].filter(Boolean) as { Component: typeof ThreadStatusBadge; key: string }[];

  if (badges.length === 0) return null;

  return (
    // Lives with the action controls at every width rather than trailing the
    // title. Tying it to the title meant its position depended on where the
    // title happened to end, which moves with the thread's name and the
    // truncation point; anchored here it lands in the same place every time.
    //
    // That also means one alignment rule instead of two: everything in this row
    // centres, so neither form has to reconcile itself with a text baseline.
    <div className="flex items-center gap-2 shrink-0">
      {/* Full pill — only where there is room for the words. `mx-1.5` stands in
          for the padding an icon button has built in, so the gap either side of
          it measures the same as the gaps further along the row. */}
      <div className="mx-1.5 flex items-center gap-2 @max-2xl:hidden">
        {badges.map((b) => (
          <b.Component key={b.key} thread={thread} />
        ))}
      </div>
      {/* Bare glyph everywhere else — the second of exactly two forms. */}
      <div className="flex items-center gap-1 @2xl:hidden">
        {badges.map((b) => (
          <b.Component key={`${b.key}-glyph`} thread={thread} compact headerScale />
        ))}
      </div>
    </div>
  );
};

/**
 * The thread's conversation column — its own header (back button + editable
 * title + facepile + thread actions) over the message list. Self-contained
 * including the loading and error states: the calendar canvas is
 * thread-query-independent, so the page renders the split immediately and this
 * panel confines the thread query's loader/error to its own body (rather than
 * blanking the whole viewport, which broke canvas continuity on a cold
 * home→thread navigation).
 */
export const ThreadDetailPanel = ({
  threadId,
  thread,
  isLoading,
  error,
  showCanvas,
  accountSwitcher,
  onLeave,
  parentLabel,
  leading,
}: {
  /** Route thread id — available (and tracked) while `thread` is still loading. */
  threadId: number;
  thread: GetThreadResp | undefined;
  isLoading: boolean;
  error: Error | null;
  /** Gate-on desktop: the canvas companion is available (shows the
   *  show/hide-calendar toggle). */
  showCanvas: boolean;
  /**
   * Account switcher, hoisted here when nothing else on the screen is showing
   * it. The canvas host passes nothing — its own chrome carries one — but on
   * `/threads/:id` with no canvas beside it, this header is the only place left
   * for it, and without it a phone has no way to change accounts at all.
   */
  accountSwitcher?: ReactNode;
  /**
   * Where back and Escape go. Omitted, the panel leaves the way it arrived —
   * browser history, which is right for `/threads/:id`. Supplied, the panel is
   * embedded in a column its host owns (home's canvas), and the host puts the
   * column back to whatever it was showing instead of navigating the page away
   * from underneath itself.
   *
   * `replace` asks the host to leave without keeping this thread's address:
   * the panel passes it when the conversation no longer exists, so Back can't
   * return to a thread that isn't there.
   */
  onLeave?: (options?: { replace?: boolean }) => void;
  /**
   * The list this conversation was drilled into from, shown before its title as
   * `Parent ›` and clickable to go back. Only meaningful alongside `onLeave` —
   * without a host to return to there is nothing for the trail to name.
   */
  parentLabel?: string;
  /** Page chrome that rides the header's leading edge, ahead of the back
   *  button — on home's canvas, the control that reopens a collapsed sidebar.
   *  Same slot and same order the pivot views' shared frame uses. */
  leading?: ReactNode;
}) => {
  const [, setMessageListScrollPosition] = useAtom(messageListScrollPositionAtom);
  const { isEventView } = useEventView();
  useEventViewHotkeys();

  useTrackThreadDetailView(threadId, thread);

  // Notify native WebView that thread content is ready to display.
  // Fires for each thread by tracking which threadId was last signaled.
  const lastSignaledThreadIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isLoading && thread && lastSignaledThreadIdRef.current !== thread.id) {
      lastSignaledThreadIdRef.current = thread.id;
      window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "threadReady" }));
    }
  }, [isLoading, thread]);

  // Held here rather than inside the title, because the overflow menu can start
  // a rename and it is the panel that renders both.
  const [isRenaming, setIsRenaming] = useState(false);
  const stopRenaming = useCallback(() => setIsRenaming(false), []);
  const startRenaming = useCallback(() => setIsRenaming(true), []);

  // Wouter re-renders this same instance when only the `:threadId` param
  // changes, so an open rename would otherwise survive the switch and greet the
  // next thread with its header already in an edit field.
  const [renamingThreadId, setRenamingThreadId] = useState(threadId);
  if (renamingThreadId !== threadId) {
    setRenamingThreadId(threadId);
    setIsRenaming(false);
  }

  // Returning false tells `useEscapeToBack` the gesture is handled here, so it
  // does not also pop history — the embedded host stays on its own page.
  const handleBack = useCallback(() => {
    setMessageListScrollPosition(-1);
    if (!onLeave) return true;
    onLeave();
    return false;
  }, [setMessageListScrollPosition, onLeave]);

  // Leaving a conversation that is gone rather than one the user is done with.
  // Separate from `handleBack` because the two differ in what they leave
  // behind: a thread you backed out of is still there to return to, a deleted
  // one is an address that now answers 403, so the host replaces its history
  // entry instead of stacking the destination on top of it. Returns `true` when
  // there is no host, leaving the caller to navigate — same contract as
  // `handleBack`, so the caller treats both exits alike.
  const handleThreadGone = useCallback(() => {
    setMessageListScrollPosition(-1);
    if (!onLeave) return true;
    onLeave({ replace: true });
    return false;
  }, [setMessageListScrollPosition, onLeave]);

  // A host that hands us `onLeave` has its own way out — on the canvas that is
  // the list sitting beside the conversation — so the header shows the trail
  // back rather than a second chevron: the same `Parent ›` a drilled-into
  // routine or contact wears, because arriving at a conversation from a list is
  // the same move. The standalone `/threads/:id` route has no list at all, and
  // there the chevron is the only exit, so it keeps it.
  //
  // None of that holds on a phone, which shows one panel at a time: the list the
  // trail names is not beside the conversation, it is the screen the
  // conversation replaced. So a phone keeps the chevron a stack expects — the
  // same split `CanvasPivotPanel` makes for a drilled-in child. Without it a
  // conversation opened where the host names no parent (a notification, a shared
  // link, a reload of `/threads/:id`) has no way back on screen at all.
  const isMobile = useIsMobile();
  const hostHandlesLeaving = !!onLeave && !isMobile;
  // Escape leaves the conversation either way. Registered here rather than only
  // inside the button, which is not rendered on the canvas; disabled when the
  // button is present, since `useShortcut` binds per call site and both would
  // answer the same key.
  //
  // Its return value, not `handleBack` itself, is what `u` below binds:
  // `useEscapeToBack` composes the veto contract with the history pop, and the
  // two keys have to leave by the same route or the standalone page's `u` would
  // reset the scroll position and then go nowhere.
  const leaveThread = useEscapeToBack(handleBack, hostHandlesLeaving);

  // `u` — the Gmail-style "back to the list". The registry's bare-key gating
  // keeps it out of the composer, which this panel hosts.
  //
  // It defers to an open Radix layer for the same reason Escape does, and more
  // sharply: a menu treats a printable key as typeahead, so `u` aimed at an item
  // would leave the thread instead of jumping to it. `useShortcut`'s own bare-key
  // guard stops at dialogs and does not cover the header's dropdown.
  useShortcut(
    "threads.back",
    () => {
      if (isFloatingLayerOpen()) return;
      leaveThread();
    },
    { deps: [leaveThread] },
  );

  // A host that owns leaving supplies the trail back, and only when there is a
  // parent to name; on its own route the panel carries the chevron. Chosen above
  // the markup because the two conditions nest, and a nested ternary in JSX is
  // the shape this codebase rules out.
  let leadingControl: ReactNode = (
    <SplitPanelBackButton onBack={handleBack} className={panelHeaderControlClass} />
  );
  if (hostHandlesLeaving) {
    leadingControl = parentLabel ? (
      <PanelBreadcrumb
        parentLabel={parentLabel}
        onBack={handleBack}
        statsigName="thread-canvas-back"
      />
    ) : null;
  }

  const header = (
    <SplitPanelHeader>
      {leading}
      {leadingControl}
      {thread && (
        // `pr-4` keeps a truncated title from butting up against the controls;
        // the header's own gap is too tight to read as separation.
        //
        // No leading pull: a thread title starts on the same gutter every
        // canvas page title does.
        <div className="flex min-w-0 flex-1 items-center pr-4">
          <ThreadTitle
            thread={thread}
            isRenaming={isRenaming}
            onRenameStart={startRenaming}
            onRenameDone={stopRenaming}
          />
        </div>
      )}
      {/* `gap-0.5` is the panel-header convention for a control group: each
          control already carries ~6px of its own padding, so
          the gap reads at roughly three times its value and a larger one scatters
          the group. */}
      {/* `panelTrailingIconClass` mirrors the back button's own pull. Both take
          a control box out past the gutter so the glyph inside it — not the
          invisible box — lines up with the panel's inset. It is 6px because a
          16px glyph centres in a 28px box; it was 8px when the box was 32 and
          the glyph 20, and that stale number left the trailing glyph sitting 2px
          inside where every other panel edge does. */}
      <div className={cn("ml-auto flex shrink-0 items-center gap-0.5", panelTrailingIconClass)}>
        {/* Leads the group: it is the one thing here that reports rather than
            acts, so it sits before the controls instead of among them. */}
        {accountSwitcher && <div className="mr-1.5">{accountSwitcher}</div>}
        {thread && <ThreadIndicators thread={thread} />}
        {/* `mx-1.5` for the same reason as the status pill: the chip carries no
            padding of its own, so without it the facepile sits tight against the
            first button while the buttons sit evenly apart from each other. */}
        {thread && (
          <ThreadHeaderFacepile thread={thread} className={cn("mx-1.5", BAR_TIER_CLASS.xl)} />
        )}
        {/* Keyed on the thread: wouter re-renders this same instance when only
            the `:threadId` param changes, so without a key an open delete
            confirmation would survive the switch and re-target whichever thread
            loaded next — the dialog still naming the old one. */}
        {thread && (
          <HeaderActions
            key={thread.id}
            thread={thread}
            onBack={handleBack}
            onThreadGone={handleThreadGone}
            showCanvas={showCanvas}
            onRename={startRenaming}
          />
        )}
        {/* Last in the group, and empty until the utilities portal in —
            `empty:hidden` so it adds no gap on a build without Marker.io.

            `mr-1.5` cancels the group's own pull for this one control. That pull
            exists so a ghost glyph, not its invisible box, lands on the gutter;
            this button is filled, so its box is what the eye reads as the edge
            and hanging it 6px past the inset reads as a misalignment. When the
            slot is empty the pull reaches whatever ends the row instead, which
            is a glyph again. */}
        <HeaderUtilitySlot className="mr-1.5" />
      </div>
    </SplitPanelHeader>
  );

  let body: ReactNode;
  if (error) {
    body = <ErrorMessage error={error} />;
  } else if (isLoading || !thread) {
    body = (
      <div className="flex flex-1 min-h-0 items-center justify-center">
        <Loader />
      </div>
    );
  } else {
    body = (
      <MessageList
        thread={thread}
        // A thread whose conversation moved to its group agent can't send.
        readOnly={isThreadSupersededByGroup(thread)}
        onEscapeKeyPressed={handleBack}
        focusType={thread.activeMessage ? "desktop-only" : "always"}
        contentWrapperClassName={pageColumnClasses["8"]}
        isEventView={isEventView}
        // In the split layout the list is a narrower embedded pane — auto-hide the
        // scrollbar (fading thumb) rather than reserving an always-visible gutter.
        // Full-screen keeps "scroll" so the gutter doesn't shift wide-screen layout.
        scrollOverflowMode={showCanvas ? "auto" : "scroll"}
      />
    );
  }

  return <SplitPanelFrame header={header}>{body}</SplitPanelFrame>;
};
